import {
  forwardRef,
  type HTMLProps,
  type LegacyRef,
  useState,
  useEffect,
} from 'react';
import type { ApiReturnCompany, ApiSite } from '@highimpact-frontend/api-types';
import SiteService from '../../api/services/config/site/SiteService';
import { getOrElse } from '../../utils/functions';
import CoordianatesPicker from '../utils/coordiantesPicker/CoordianatesPicker';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import type { coordinates, Point } from '../../utils/geom/types';
import { resetExternalState } from '../../store/data/slice';
import { useAppDispatch } from '../../store/hooks';
import CompanyService from '../../api/services/config/company/CompanyService';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import DropdownItemsSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import { Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import PermissionDecoratorV2 from '../../components/utils/permission/PermissionDecoratorV2';
import TimezoneSelector from './TimezoneSelector';
import DropDownMultiAutocompleteComponent from '../utils/MUISpecifics/dropDownMultiSelector/dropDownAutocompleteMultiSelector';
import ProjectService from '../../api/services/config/project/ProjectService';
import UserService from '../../api/services/config/user/UserService';
import UserSiteService from '../../api/services/config/user_site/UserSiteService';
import { isAxiosError } from 'axios';

// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  onCancel: () => void;
  isOpen: boolean;
}

const defaultValues: ApiSite = {
  name: '',
  surface: 0,
  associated_projects_ids: [],
  position: null,
  france_weather_station_id: null,
  timezone: null,
};

// Component.
function CreateSiteForm(
  { isOpen: isModalOpen, children, onCancel, ...props }: FormProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // use translation here:
  const { t } = useTranslation('components.sites');

  const dispatch = useAppDispatch();
  const { submit, state } = useSubmit();
  const [projects, setProjects] = useState<any[]>([]);
  const [companies, setCompanies] = useState<ApiReturnCompany[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  );
  const [projectUsers, setProjectUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);

  const onSubmit = async (data: ApiSite) => {
    submit({
      callbacks: [
        async () => {
          const site = await SiteService.add(data);
          await Promise.all(
            selectedUsers.map((user) => {
              try {
                UserSiteService.add({
                  user_id: user.id,
                  site_id: site.id,
                })
              } catch (error) {
                if (!isAxiosError(error) || error.response?.status !== 404) {
                  throw error;
                }
                UserSiteService.add({ user_id: user.id, site_id: site.id })
              }
            }
            )
          );
        },
        () => methods.reset(),
        () => onCancel(),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => dispatch(resetExternalState({ external_key: 'project_sites' })),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () =>
          dispatch(
            resetExternalState({ external_key: 'device_measure_types' })
          ),
      ],
      errorMessages: {
        anyError: t('Une erreur est survenue lors de la création du site'),
        unicityError: t('Un site du même nom existe déjà dans ce projet'),
      },
      successMessage: t('Le site a été créé avec succès'),
    });
  };

  /**
   * Create validation rule for each field.
   */
  const validator = createValidator<ApiSite>({
    name: [
      (n) => (n.length <= 3 ? t('Entrer au moins 4 caractères') : undefined),
    ],
    surface: [],
    perimeter: [],
    address: [],
    position: [
      (n) => (n === null ? t('Sélectionner une position') : undefined),
    ],
    associated_projects_ids: [
      (n) =>
        n.length === 0 ? t('Sélectionner au moins un projet') : undefined,
    ],
    company_id: [(n) => (!n ? t('Sélectionner une entreprise') : undefined)],
    france_weather_station_id: [
      (n) => {
        if (n === null || n === undefined || n === '') return undefined;
        const value = String(n);
        return /^\d{8}$/.test(value)
          ? undefined
          : t('Entrer exactement 8 chiffres ou laisser le champ vide');
      },
    ],
  });

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);
  const position = methods.watch('position');
  const timezoneCoordinates =
    position?.coordinates != null
      ? {
        lat: Number(position.coordinates[0]),
        lng: Number(position.coordinates[1]),
      }
      : null;

  /**
   * Retrieve coordinates.
   */
  const handleCoordinateSelect = (
    value: { lat: number; lng: number },
    area: number,
    address: Record<string, string> | undefined
  ) => {
    // Créer la chaîne WKT avec la longitude et la latitude
    const point: Point<coordinates, coordinates> = {
      type: 'POINT',
      coordinates: [value.lat, value.lng],
    };

    //clean strings from falsy values
    const cleanString = (v?: string) =>
      !v || v === 'undefined' || v === 'null' ? '' : v;

    methods.setMultipleValues({
      position: point,
      surface: area,
      address: [cleanString(address?.house_number), cleanString(address?.road)]
        .filter(Boolean)
        .join(' ')
        .trim(),
      city: cleanString(address?.city ?? address?.town),
      postal_code: address?.postcode ? Number(address.postcode) : null,
    });
  };

  useEffect(() => {
    CompanyService.getCompanies()
      .then((companies) => {
        setCompanies(companies);
      })
      .catch((error) => {
        console.error('Error fetching companies:', error);
      });
    ProjectService.getProjects(true)
      .then((projects) => {
        setProjects(projects);
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
      });
  }, []);

  return (
    <div className="customized-dialog-wrapper">
      <CustomizedDialog
        title={t('Créer un nouveau site')}
        open={isModalOpen}
        onCancel={() => {
          onCancel();
          methods.reset();
        }}
      >
        <form
          onSubmit={methods.onSubmit}
          className={`form ${props.className || ''}`}
        >
          <Stack spacing={2}>
            <TextInput
              onChange={(value) => {
                methods.setValue('name', value);
              }}
              label={t('Nom du site')}
              name={'name'}
              placeholder="Entrer un nom"
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
            />
            <DropdownItemsSelector
              onSelect={async (selectedId) => {
                setSelectedProjectId(selectedId);
                methods.setValue(
                  'associated_projects_ids',
                  selectedId === null ? [] : [selectedId]
                );
                if (selectedId === null) {
                  setProjectUsers([]);
                  setSelectedUsers([]);
                  return;
                }
                try {
                  const projectUsers =
                    await UserService.getUsersByProjectId(selectedId);
                  const mappedUsers = projectUsers.map((user) => ({
                    id: user.id,
                    name: user.name + ' ' + user.surname,
                  }));
                  setProjectUsers(mappedUsers);
                  setSelectedUsers(mappedUsers);
                } catch (error) {
                  console.error('Error fetching users:', error);
                  setProjectUsers([]);
                  setSelectedUsers([]);
                }
              }}
              values={projects.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              label={t('Projet')}
              disabled={false}
              name={'project_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                    tag: 'Submited',
                    errors: methods.getErrors('associated_projects_ids'),
                  }
                  : { tag: 'Not submitted' }
              }
            />
            <DropDownMultiAutocompleteComponent
              label={t('Utilisateurs associés au site')}
              options={projectUsers}
              value={selectedUsers}
              onChange={setSelectedUsers}
              disabled={selectedProjectId === null}
            />
            <DropdownItemsSelector
              onSelect={(selectedId) => {
                methods.setValue('company_id', selectedId);
              }}
              values={companies.map((company) => ({
                id: company.id,
                name: getOrElse(company.name),
              }))}
              label={t("Nom de l'exploitant")}
              disabled={false}
              name={'company_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                    tag: 'Submited',
                    errors: methods.getErrors('company_id'),
                  }
                  : { tag: 'Not submitted' }
              }
            />
            <PermissionDecoratorV2 item="france_station_input">
              <TextInput
                onChange={(value) => {
                  methods.setValue('france_weather_station_id', value);
                }}
                label={t('Identifiant de la station Météo France')}
                name={'id_france_weather_station'}
                placeholder="Entrer un identifiant"
                helperText={t(
                  "Si votre site n'est pas concerné par les données Météo France, ne renseignez pas le champ Identifiant de la station Météo France"
                )}
                helperVariant="warning"
                errors={
                  methods.hasSubmitted
                    ? {
                      tag: 'Submited',
                      errors: methods.getErrors(
                        'france_weather_station_id'
                      ),
                    }
                    : { tag: 'Not submitted' }
                }
              />
            </PermissionDecoratorV2>
            <CoordianatesPicker
              label={t('Position du site')}
              onClick={(value, area, address) =>
                handleCoordinateSelect(value, area, address)
              }
              context={t('du site')}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('position') }
                  : { tag: 'Not submitted' }
              }
            />
            <TimezoneSelector
              value={methods.watch('timezone')}
              onSelect={(timezone) => methods.setValue('timezone', timezone)}
              coordinates={timezoneCoordinates}
              label={t('Fuseau horaire')}
            />
            <Button
              type="submit"
              variant="outlined"
              className="form-button-submit"
              data-testid="submit-site-button"
              loadingPosition="end"
              endIcon={<SaveIcon />}
            >
              {' '}
              {state === 'Loading' ? t('Envoi...') : t('Créer')}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(CreateSiteForm);
